import * as THREE from 'three';
import { params, degToRad, clampPitchDeg } from './config.js';

// Scene setup
export const scene = new THREE.Scene();
scene.background = new THREE.Color(0x0e0f13);

// These will be initialized in initScene()
export let canvasContainer = null;
export let camera = null;
export let renderer = null;
export let clock = null;
export let ambient = null;
export let dir = null;
export let grid = null;

// Initial camera state for reset
export let initialCamPos = null;
export let initialCamQuat = null;
export let defaultCamPos = null;
export let defaultCamQuat = null;

export function initScene() {
  try {
    // Get canvas container
    canvasContainer = document.getElementById('canvas-container');

    if (!canvasContainer) {
      console.error('canvas-container element not found!');
      return false;
    }

    console.log('Canvas container found:', canvasContainer);
    console.log('Container dimensions:', canvasContainer.clientWidth, 'x', canvasContainer.clientHeight);

    // Ensure container has dimensions
    const width = canvasContainer.clientWidth || window.innerWidth;
    const height = canvasContainer.clientHeight || window.innerHeight;

    // Camera
    camera = new THREE.PerspectiveCamera(
      params.camFov,
      width / height,
      0.1,
      1000
    );
    camera.position.set(0, 10, 18);
    console.log('Camera created');

    // Renderer
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true });
    } catch (webglErr) {
      console.error('WebGL not supported:', webglErr);
      canvasContainer.innerHTML = '<div style="color:white;padding:20px;">WebGL not supported in this browser</div>';
      return false;
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    canvasContainer.appendChild(renderer.domElement);
    renderer.domElement.classList.add('drop-target');
    console.log('Renderer created and attached, canvas:', renderer.domElement);

    // Clock
    clock = new THREE.Clock();

    // Lights
    ambient = new THREE.AmbientLight(0xffffff, 0.45);
    scene.add(ambient);

    dir = new THREE.DirectionalLight(0xffffff, 1.1);
    dir.position.set(5, 10, 7);
    scene.add(dir);

    // Ground grid for scale reference
    grid = new THREE.GridHelper(200, 200, 0x33353a, 0x1e2026);
    grid.material.opacity = 0.35;
    grid.material.transparent = true;
    grid.material.depthWrite = false;
    grid.material.depthTest = false;
    grid.visible = false;
    scene.add(grid);

    // Initial camera state for reset
    initialCamPos = new THREE.Vector3().copy(camera.position);
    initialCamQuat = new THREE.Quaternion().copy(camera.quaternion);

    console.log('Scene initialization complete');
    return true;
  } catch (err) {
    console.error('Error in initScene:', err);
    return false;
  }
}

export function setDefaultCameraState() {
  if (camera) {
    defaultCamPos = new THREE.Vector3().copy(camera.position);
    defaultCamQuat = new THREE.Quaternion().copy(camera.quaternion);
  }
}

export function getDefaultCamPos() {
  return defaultCamPos;
}

export function getDefaultCamQuat() {
  return defaultCamQuat;
}

// Apply yaw and pitch in degrees
export function applyYawPitchDeg(yawDeg, pitchDeg) {
  if (!camera) return;
  const yaw = degToRad(yawDeg);
  const pitch = degToRad(clampPitchDeg(pitchDeg));
  const e = new THREE.Euler(pitch, yaw, 0, 'YXZ');
  camera.quaternion.setFromEuler(e);
}

// Apply fog from params
export function applyFogFromParams() {
  if (params.fogEnabled) {
    scene.fog = new THREE.Fog(
      params.fogColor,
      params.fogStart,
      params.fogEnd
    );
    scene.background = new THREE.Color(params.fogColor);
  } else {
    scene.fog = null;
  }
}

// Resize handling
export function setupResizeHandler() {
  window.addEventListener('resize', () => {
    if (!canvasContainer || !camera || !renderer) return;
    const width = canvasContainer.clientWidth;
    const height = canvasContainer.clientHeight;
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height);
  });
}
